import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import { HtmlTemplatePreview } from './HtmlTemplatePreview';

/**
 * ProfessionalPagePreview — Prévia em tempo real da Página Profissional
 *
 * Renderiza o preview dentro de um container que simula um dispositivo mobile.
 * Usa HtmlTemplatePreview quando o template selecionado possui htmlUrl; caso
 * contrário exibe um fallback estático simples.
 *
 * Props:
 * - formData    : objeto com todos os campos do formulário (via useWatch)
 * - templateId  : GUID do template selecionado
 * - templates   : lista de templates carregados para o tipo "professional"
 * - loading     : boolean — exibe skeletons enquanto dados são carregados
 */
const ProfessionalPagePreview = ({
  formData = {},
  templateId,
  templates = [],
  loading = false,
}) => {
  const [viewMode, setViewMode] = useState('mobile');
  // Template selecionado a partir da lista
  const selectedTemplate = useMemo(
    () => templates.find(t => t.id === (templateId || formData.templateId)),
    [templates, templateId, formData.templateId],
  );

  // Estilos customizados para o container externo
  const customStyles = formData.customStyles || {};
  const bgColor = customStyles.backgroundColor || '#ffffff';

  // Monta objeto de dados normalizado para o HtmlTemplatePreview
  const previewData = useMemo(() => ({
    displayName:        formData.displayName        || 'Nome do Profissional',
    title:              formData.title              || '',
    specialty:          formData.specialty          || 'Especialidade',
    registrationNumber: formData.registrationNumber || '',
    aboutHtml:          formData.aboutHtml          || '',
    photoUrl:           formData.photoUrl           || '',
    avatarShape:        formData.avatarShape        || 'round',
    contact: {
      phone:    formData['contact.phone']    || formData.contact?.phone    || '',
      whatsapp: formData['contact.whatsapp'] || formData.contact?.whatsapp || '',
      email:    formData['contact.email']    || formData.contact?.email    || '',
      website:  formData['contact.website']  || formData.contact?.website  || '',
    },
    socialLinks: {
      instagram: formData['socialLinks.instagram'] || formData.socialLinks?.instagram || '',
      linkedin:  formData['socialLinks.linkedin']  || formData.socialLinks?.linkedin  || '',
      twitter:   formData['socialLinks.twitter']   || formData.socialLinks?.twitter   || '',
      youtube:   formData['socialLinks.youtube']   || formData.socialLinks?.youtube   || '',
      facebook:  formData['socialLinks.facebook']  || formData.socialLinks?.facebook  || '',
      tiktok:    formData['socialLinks.tiktok']    || formData.socialLinks?.tiktok    || '',
      threads:   formData['socialLinks.threads']   || formData.socialLinks?.threads   || '',
    },
    booking: {
      placeProfessionalIds: formData.booking?.placeProfessionalIds || [],
      showBookingSection:   formData.booking?.showBookingSection   ?? true,
    },
    experience:   formData.experience   || [],
    services:     formData.services     || [],
    testimonials: formData.testimonials || [],
    customStyles: {
      primaryColor:    customStyles.primaryColor    || '#0d6efd',
      backgroundColor: customStyles.backgroundColor || '#ffffff',
      textColor:       customStyles.textColor       || '#212529',
      fontFamily:      customStyles.fontFamily      || 'Inter',
      fontSize:        customStyles.fontSize        || 'medium',
    },
  }), [formData, customStyles]);

  // --- Estado de carregamento ---
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: 500 }}>
        <Stack spacing={2} sx={{ width: '100%', maxWidth: 390, p: 2 }}>
          <Skeleton variant="circular" width={100} height={100} sx={{ mx: 'auto' }} />
          <Skeleton variant="text" width="60%" sx={{ mx: 'auto' }} />
          <Skeleton variant="text" width="40%" sx={{ mx: 'auto' }} />
          <Skeleton variant="rectangular" height={120} />
          <Skeleton variant="rectangular" height={60} />
          <Skeleton variant="rectangular" height={60} />
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minHeight: '100%',
        p: 2,
        bgcolor: '#e8e8e8',
        overflowY: 'auto',
        gap: 2,
      }}
    >
      {/* Toggle Mobile / Desktop */}
      <ToggleButtonGroup
        value={viewMode}
        exclusive
        onChange={(_, v) => v && setViewMode(v)}
        size="small"
        sx={{ bgcolor: '#fff', borderRadius: 2, boxShadow: 1 }}
      >
        <ToggleButton value="mobile" aria-label="Celular">
          <PhoneIphoneIcon fontSize="small" sx={{ mr: 0.5 }} />
          Celular
        </ToggleButton>
        <ToggleButton value="desktop" aria-label="Desktop">
          <DesktopWindowsIcon fontSize="small" sx={{ mr: 0.5 }} />
          Desktop
        </ToggleButton>
      </ToggleButtonGroup>

      {viewMode === 'mobile' ? (
        /* ─────────────── Moldura MOBILE ─────────────── */
        <Box
          sx={{
            width: 390,
            height: 760,
            border: '8px solid #1a1a1a',
            borderRadius: '40px',
            overflow: 'hidden',
            position: 'relative',
            backgroundColor: bgColor,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            flexShrink: 0,
          }}
        >
          {/* Entalhe da câmera (notch decorativo) */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 100,
              height: 24,
              bgcolor: '#1a1a1a',
              borderBottomLeftRadius: 12,
              borderBottomRightRadius: 12,
              zIndex: 10,
            }}
          />
          {/* Área de conteúdo (abaixo do notch) */}
          <Box sx={{ height: '100%', overflowY: 'auto', pt: '24px' }}>
            {selectedTemplate?.htmlUrl ? (
              <HtmlTemplatePreview
                templateUrl={selectedTemplate.htmlUrl}
                formData={previewData}
              />
            ) : (
              <FallbackPreview
                formData={formData}
                hasTemplate={!!selectedTemplate}
                customStyles={customStyles}
              />
            )}
          </Box>
        </Box>
      ) : (
        /* ─────────────── Moldura DESKTOP ─────────────── */
        <Box
          sx={{
            width: '100%',
            maxWidth: 900,
            border: '1.5px solid #c0c0c0',
            borderRadius: '10px',
            overflow: 'hidden',
            backgroundColor: bgColor,
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            flexShrink: 0,
          }}
        >
          {/* Barra do browser simulada */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.5,
              height: 36,
              bgcolor: '#e4e4e4',
              borderBottom: '1px solid #c0c0c0',
            }}
          >
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ff5f57' }} />
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ffbd2e' }} />
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#28c840' }} />
            <Box
              sx={{
                flex: 1,
                mx: 2,
                height: 20,
                bgcolor: '#fff',
                borderRadius: 1,
                border: '1px solid #c8c8c8',
              }}
            />
          </Box>
          {/* Conteúdo desktop */}
          <Box sx={{ height: 680, overflowY: 'auto', bgcolor: bgColor }}>
            {selectedTemplate?.htmlUrl ? (
              <HtmlTemplatePreview
                templateUrl={selectedTemplate.htmlUrl}
                formData={previewData}
              />
            ) : (
              <FallbackPreview
                formData={formData}
                hasTemplate={!!selectedTemplate}
                customStyles={customStyles}
              />
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

/* ──────────────────────────────────────────────────────────────────────────
   Componente interno: exibido quando não há template com htmlUrl selecionado
   ────────────────────────────────────────────────────────────────────────── */
const FallbackPreview = ({ formData, hasTemplate, customStyles }) => {
  const bgColor   = customStyles.backgroundColor || '#ffffff';
  const textColor = customStyles.textColor       || '#212529';
  const primary   = customStyles.primaryColor    || '#0d6efd';
  const font      = customStyles.fontFamily      || 'Inter, sans-serif';

  const hasProfessional = !!formData.displayName;

  return (
    <Box
      sx={{
        height: '100%',
        backgroundColor: bgColor,
        color: textColor,
        fontFamily: font,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: 3,
        overflowY: 'auto',
      }}
    >
      {/* Foto / Avatar */}
      {formData.photoUrl ? (
        <Box
          component="img"
          src={formData.photoUrl}
          alt={formData.displayName || 'Profissional'}
          sx={{
            width: 88,
            height: 88,
            borderRadius: formData.avatarShape === 'round' ? '50%' : 2,
            objectFit: 'cover',
            mb: 2,
            border: `3px solid ${primary}`,
          }}
        />
      ) : (
        <Avatar
          sx={{
            width: 88,
            height: 88,
            bgcolor: primary,
            mb: 2,
          }}
        >
          <PersonIcon sx={{ fontSize: 48 }} />
        </Avatar>
      )}

      {/* Nome e título */}
      <Typography variant="h6" sx={{ fontWeight: 700, textAlign: 'center', color: textColor, mb: 0.5 }}>
        {formData.displayName || 'Nome do Profissional'}
      </Typography>

      {formData.specialty && (
        <Typography variant="body2" sx={{ color: textColor, opacity: 0.75, textAlign: 'center', mb: 0.5 }}>
          {formData.specialty}
        </Typography>
      )}

      {formData.registrationNumber && (
        <Typography variant="caption" sx={{ color: textColor, opacity: 0.55, mb: 2 }}>
          {formData.registrationNumber}
        </Typography>
      )}

      {/* Mensagem de seleção de template */}
      {!hasTemplate ? (
        <Box
          sx={{
            mt: 4,
            p: 3,
            border: `1.5px dashed ${primary}`,
            borderRadius: 2,
            textAlign: 'center',
            width: '100%',
          }}
        >
          <Typography variant="body2" sx={{ color: textColor, opacity: 0.65 }}>
            Selecione um template para visualizar o layout da página
          </Typography>
        </Box>
      ) : (
        /* Template selecionado mas sem htmlUrl — informa ao usuário */
        <Box
          sx={{
            mt: 4,
            p: 3,
            border: `1.5px dashed ${primary}`,
            borderRadius: 2,
            textAlign: 'center',
            width: '100%',
          }}
        >
          <Typography variant="body2" sx={{ color: textColor, opacity: 0.65 }}>
            Preview do template disponível após publicação
          </Typography>
        </Box>
      )}

      {/* Sobre — trecho do texto (sem HTML) */}
      {formData.aboutHtml && hasProfessional && (
        <Box sx={{ mt: 3, width: '100%' }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: primary, textTransform: 'uppercase', letterSpacing: 1 }}>
            Sobre
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: textColor, mt: 1, opacity: 0.8 }}
            dangerouslySetInnerHTML={{
              __html: formData.aboutHtml.replace(/<[^>]+>/g, ' ').substring(0, 200) + (formData.aboutHtml.length > 200 ? '…' : ''),
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default ProfessionalPagePreview;
